/**
 * Arkada oʻyinlar (demo `arcadeShell`/`arcadeEnd`/`startT60`/`startMeteor`).
 * Tezkor 60 — React holati; Meteor — DOM-ogʻir, demo mantiqi `useEffect` ichida imperativ (sky ref).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { W, distract, fmtTime, shuffle } from '../../data';
import type { Word } from '../../data/types';
import { S, bump, useStore } from '../../store';
import { nav } from '../../lib/nav';
import { boop, chime, pew } from '../../lib/sound';
import { speak } from '../../lib/speech';
import { haptic } from '../../lib/telegram';
import { applyCourse, fire, sync } from '../../lib/sync';
import { t, tn } from '../../i18n';
import { XPR, estXp } from '../../lib/xp';
import { Ic } from '../../ui/Icon';

type ArcadeGame = 't60' | 'meteor';

/** Demo `arcadeShell`: sbar (×, sarlavha, stat) + sbody. */
function ArcadeShell({ title, stat, children }: { title: string; stat: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Chiqish')} onClick={() => nav.home('games')}><Ic n="x" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 15 }}>{t(title)}</b>
        {stat}
      </div>
      <div className="sbody fade" style={{ gap: 18 }}>{children}</div>
    </>
  );
}

/** Oʻyin yakuni: rekordni saqlash, XP, natija ekrani. */
function endGame(game: ArcadeGame, pool: Word[], score: number) {
  const key = game === 't60' ? 'best60' : 'bestMet';
  const prev = S()[key];
  const isRec = !prev || score > prev;
  if (isRec) S().set({ [key]: score });
  const xp = estXp(Math.min(XPR.gameCap, game === 't60' ? score * 2 : Math.round(score / 3)));
  bump(xp);
  fire(sync.game({ game, score }).then((r) => applyCourse(r.course)), 'game');
  nav.replace({
    k: 'arcadeEnd', title: game === 't60' ? 'Tezkor 60' : 'Meteor', score, best: S()[key] ?? score, isRec: isRec && score > 0, xp,
    again: () => nav.replace({ k: 'arcade', game, pool }),
  });
}

export function Arcade({ game, pool }: { game: ArcadeGame; pool: Word[] }) {
  return game === 't60' ? <T60 pool={pool} /> : <Meteor pool={pool} />;
}

/* ---------- Tezkor 60 ---------- */
function T60({ pool }: { pool: Word[] }) {
  const [score, setScore] = useState(0);
  const [left, setLeft] = useState(60);
  const [q, setQ] = useState<{ w: Word; shown: Word; truth: boolean } | null>(null);
  const [flash, setFlash] = useState('');
  const R = useRef({ score: 0, alive: true, cur: null as { w: Word; truth: boolean } | null, streak: 0 });

  const nextQ = () => {
    const w = pool[Math.floor(Math.random() * pool.length)]!;
    const truth = Math.random() < 0.5;
    const shown = truth ? w : (distract(w, 'uz', 1)[0] ?? w);
    R.current.cur = { w, truth };
    setQ({ w, shown, truth }); setFlash('');
  };
  const ans = (v: boolean) => {
    const r = R.current; if (!r.alive || !r.cur) return;
    const ok = v === r.cur.truth;
    setFlash(ok ? 'flashok' : 'flashbad');
    if (ok) { r.score++; r.streak++; setScore(r.score); S().set({ right: S().right + 1 }); }
    else { r.streak = 0; boop(); S().set({ wrong: S().wrong + 1 }); haptic.bad(); }
    setTimeout(() => { if (r.alive) nextQ(); }, ok ? 160 : 420);
  };
  const ansRef = useRef(ans); ansRef.current = ans;

  useEffect(() => {
    const r = R.current; r.alive = true;
    nextQ();
    const key = (e: KeyboardEvent) => { if (e.key === 'ArrowRight') ansRef.current(true); if (e.key === 'ArrowLeft') ansRef.current(false); };
    document.addEventListener('keydown', key);
    let l = 60;
    const tm = setInterval(() => {
      l--; setLeft(l);
      if (l <= 0) { clearInterval(tm); document.removeEventListener('keydown', key); r.alive = false; endGame('t60', pool, r.score); }
    }, 1000);
    return () => { r.alive = false; clearInterval(tm); document.removeEventListener('keydown', key); };
  }, []); // eslint-disable-line

  return (
    <ArcadeShell title="Tezkor 60" stat={<b style={{ color: 'var(--primary-ink)', fontSize: 15 }}>{fmtTime(left)}</b>}>
      <div className="row g8">
        <span className="pill" style={{ background: 'var(--success-soft)', color: 'var(--success-ink)' }}><Ic n="check" size={16} /><span>{score}</span></span>
        <span className="t13">{t('Juftlik toʻgʻrimi? Tez javob bering.')}</span>
      </div>
      <div className={`qcard t60 ${flash}`} style={{ alignItems: 'center', textAlign: 'center', padding: '34px 20px', gap: 12 }}>
        {q && (
          <>
            <b style={{ fontSize: 32, lineHeight: '40px' }}>{q.w.w}</b>
            <span className="t13" style={{ fontSize: 13 }}>=</span>
            <b style={{ fontSize: 24, lineHeight: '32px', color: 'var(--primary-ink)' }}>{q.shown.uz}</b>
          </>
        )}
      </div>
      <div className="row g10" style={{ marginTop: 'auto' }}>
        <button className="btn tf no" onClick={() => ans(false)}><Ic n="x" size={26} />{t('Notoʻgʻri')}</button>
        <button className="btn tf yes" onClick={() => ans(true)}><Ic n="check" size={26} />{t('Toʻgʻri')}</button>
      </div>
    </ArcadeShell>
  );
}

/* ---------- Meteor ---------- */
interface Rock { el: HTMLButtonElement; o: Word; x: number; y: number; vx: number; vy: number; w: number; h: number; rot: number; vr: number; entering: boolean; dead?: boolean; frozen?: boolean }

function Meteor({ pool }: { pool: Word[] }) {
  const sky = useRef<HTMLDivElement>(null);
  const cn = useRef<HTMLDivElement>(null);
  const br = useRef<HTMLDivElement>(null);
  const pw = useRef<HTMLElement>(null);
  const tb = useRef<HTMLElement>(null);
  const lu = useRef<HTMLDivElement>(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const curRef = useRef<Word | null>(null);

  useEffect(() => {
    const skyEl = sky.current!, brEl = br.current!, cnEl = cn.current!, pwEl = pw.current!, tbEl = tb.current!, luEl = lu.current!;
    // uzluksiz: asteroidlar doim havoda; topilgani oʻrniga chetdan yangisi uchib kiradi
    let score = 0, lives = 3, level = 1, okInLevel = 0, raf = 0, cur: Word | null = null, curRock: Rock | null = null, alive = true, busy = false, rocks: Rock[] = [], last = 0, tStart = 0, lastT: Word | null = null;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => { timers.push(setTimeout(fn, ms)); };
    const N = 5;
    const fillers = W.filter((w) => !pool.includes(w));
    const roundMs = () => Math.max(5000, 12000 - (level - 1) * 1200);
    const speedK = () => 0.03 + (level - 1) * 0.012;
    const dims = () => ({ W0: skyEl.clientWidth, H0: skyEl.clientHeight - 120 });
    const onScreen = () => rocks.filter((r) => !r.dead).map((r) => r.o.id);
    const cells = shuffle([...Array(6).keys()]);
    const pickWord = (): Word => { // havoda yoʻq, oʻrganilgan soʻz; yetmasa — boshqa soʻzlar bilan toʻldiriladi
      const used = onScreen(); let c = shuffle(pool.filter((w) => !used.includes(w.id)));
      const uzOn = rocks.filter((r) => !r.dead).map((r) => r.o.uz); c = c.filter((w) => !uzOn.includes(w.uz));
      if (c.length) return c[0]!;
      const f = shuffle(fillers.filter((w) => !used.includes(w.id) && !uzOn.includes(w.uz))); return f[0] || pool[0]!;
    };
    const spawn = (o: Word, edge: boolean): Rock => {
      const { W0, H0 } = dims(); const el = document.createElement('button'); el.type = 'button'; el.className = 'ast float' + (edge ? ' enter' : '');
      const rock = document.createElement('i'); rock.className = 'rock'; const sp0 = document.createElement('span'); sp0.textContent = o.uz; el.append(rock, sp0);
      skyEl.appendChild(el); const w = el.offsetWidth, h = el.offsetHeight; const sp = speedK() * (0.7 + Math.random() * 0.6);
      let x: number, y: number, ang: number;
      if (edge) { const side = Math.floor(Math.random() * 3); // chap, oʻng yoki tepadan kiradi
        if (side === 0) { x = -w * 0.6; y = Math.random() * (H0 - h); ang = (Math.random() - 0.5) * 1.2; }
        else if (side === 1) { x = W0 - w * 0.4; y = Math.random() * (H0 - h); ang = Math.PI + (Math.random() - 0.5) * 1.2; }
        else { x = Math.random() * (W0 - w); y = -h * 0.6; ang = Math.PI / 2 + (Math.random() - 0.5) * 1.2; } }
      else { const i = rocks.length, c = cells[i] ?? 0; x = Math.max(0, Math.min(W0 - w, (c % 2) * (W0 / 2) + Math.random() * Math.max(0, W0 / 2 - w))); y = Math.max(6, Math.min(H0 - h, 6 + Math.floor(c / 2) * (H0 / 3) + Math.random() * Math.max(0, H0 / 3 - h))); ang = Math.random() * Math.PI * 2; }
      const r: Rock = { el, o, x, y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, w, h, rot: Math.random() * 360, vr: (Math.random() - 0.5) * 0.08, entering: !!edge };
      el.style.transform = `translate(${x}px,${y}px)`; el.onclick = () => pick(r); rocks.push(r); return r;
    };
    const newTarget = () => {
      const live = rocks.filter((r) => !r.dead && pool.includes(r.o)); let c = live.filter((r) => r.o !== lastT); if (!c.length) c = live;
      if (!c.length) { const r = spawn(pool[Math.floor(Math.random() * pool.length)]!, true); c = [r]; }
      curRock = c[Math.floor(Math.random() * c.length)]!; cur = curRock.o; lastT = cur; curRef.current = cur;
      pwEl.textContent = cur.w; pwEl.classList.remove('swap'); void pwEl.offsetWidth; pwEl.classList.add('swap');
      tStart = performance.now(); busy = false;
    };
    const loop = (now: number) => {
      if (!alive || !skyEl.isConnected) { alive = false; return; } const dt = Math.min(40, now - last); last = now; const { W0, H0 } = dims();
      rocks.forEach((r) => { if (r.dead || r.frozen) return; r.x += r.vx * dt; r.y += r.vy * dt; r.rot += r.vr * dt;
        if (r.entering) { if (r.x >= 0 && r.x <= W0 - r.w && r.y >= 4 && r.y <= H0 - r.h) r.entering = false; }
        else { if (r.x < 0) { r.x = 0; r.vx = Math.abs(r.vx); } if (r.x > W0 - r.w) { r.x = W0 - r.w; r.vx = -Math.abs(r.vx); }
          if (r.y < 4) { r.y = 4; r.vy = Math.abs(r.vy); } if (r.y > H0 - r.h) { r.y = H0 - r.h; r.vy = -Math.abs(r.vy); } }
        r.el.style.transform = `translate(${r.x}px,${r.y}px)`; (r.el.firstChild as HTMLElement).style.transform = `rotate(${r.rot}deg)`; });
      if (!busy) { const p = Math.min(1, (now - tStart) / roundMs()); tbEl.style.width = ((1 - p) * 100) + '%'; tbEl.classList.toggle('low', p > 0.7); if (p >= 1) timeout(); }
      raf = requestAnimationFrame(loop);
    };
    const aimAt = (r: Rock) => { const sr = skyEl.getBoundingClientRect(), cr = cnEl.getBoundingClientRect();
      const cx = cr.left + cr.width / 2 - sr.left, cy = cr.top + 12 - sr.top, tx = r.x + r.w / 2, ty = r.y + r.h / 2 - 8;
      const ang = Math.atan2(tx - cx, cy - ty) * 180 / Math.PI; brEl.style.transform = `rotate(${ang}deg)`; return { cx, cy, ang }; };
    const fire = (r: Rock, cb: (tx: number, ty: number) => void) => {
      const a = aimAt(r); cnEl.classList.remove('recoil'); void cnEl.offsetWidth; cnEl.classList.add('recoil'); pew(); r.frozen = true;
      const len = 46, sx = a.cx + Math.sin(a.ang * Math.PI / 180) * len, sy = a.cy - Math.cos(a.ang * Math.PI / 180) * len;
      const b = document.createElement('i'); b.className = 'shot'; skyEl.appendChild(b); const t0 = performance.now(), D = 200;
      const go = (now: number) => { const p = Math.min(1, (now - t0) / D), tx = r.x + r.w / 2, ty = r.y + r.h / 2 - 8; b.style.transform = `translate(${sx + (tx - sx) * p}px,${sy + (ty - sy) * p}px) rotate(${a.ang}deg)`;
        if (p < 1) requestAnimationFrame(go); else { b.remove(); if (alive) cb(tx, ty); } };
      requestAnimationFrame(go);
    };
    const sparks = (x: number, y: number, cls: string) => { for (let i = 0; i < 10; i++) { const s = document.createElement('i'); s.className = 'spark ' + cls; const an = Math.random() * Math.PI * 2, d = 30 + Math.random() * 40;
      s.style.left = x + 'px'; s.style.top = y + 'px'; s.style.setProperty('--dx', Math.cos(an) * d + 'px'); s.style.setProperty('--dy', Math.sin(an) * d + 'px'); skyEl.appendChild(s); later(() => s.remove(), 650); } };
    const replace = (r: Rock) => { r.dead = true; later(() => { r.el.remove(); rocks = rocks.filter((x) => x !== r); }, 450); spawn(pickWord(), true); };
    const flash = () => { skyEl.classList.remove('hurt', 'shake'); void skyEl.offsetWidth; skyEl.classList.add('hurt', 'shake'); };
    const pick = (r: Rock) => { if (!alive || r.dead || r.frozen) return;
      const ok = r === curRock; if (ok) busy = true;
      fire(r, (tx, ty) => {
        if (ok) { score += 10 * level; okInLevel++; S().set({ right: S().right + 1 }); setScore(score); r.el.classList.add('boom'); sparks(tx, ty, 'good'); chime();
          if (okInLevel >= 5) { level++; okInLevel = 0; setLevel(level); luEl.textContent = tn('#-daraja! Tezroq', level); luEl.hidden = false; later(() => { luEl.hidden = true; }, 1300);
            rocks.forEach((x) => { x.vx *= 1.3; x.vy *= 1.3; }); }
          replace(r); newTarget();
        } else {
          r.frozen = false; r.el.classList.remove('wob'); void r.el.offsetWidth; r.el.classList.add('wob', 'hitbad'); later(() => r.el.classList.remove('hitbad'), 600);
          sparks(tx, ty, 'bad'); const p = document.createElement('b'); p.className = 'pop'; p.textContent = '✕'; p.style.left = tx + 'px'; p.style.top = ty + 'px'; skyEl.appendChild(p); later(() => p.remove(), 700);
          flash(); miss();
        } });
    };
    const miss = () => { lives--; setLives(lives); boop(); haptic.bad(); S().set({ wrong: S().wrong + 1 });
      if (curRock && !curRock.dead) { curRock.el.classList.add('right'); const cr = curRock; later(() => cr.el && cr.el.classList.remove('right'), 900); }
      if (lives <= 0) return end(); };
    const timeout = () => { busy = true; flash(); miss(); if (alive) later(() => { if (alive) newTarget(); }, 900); };
    const end = () => { busy = true; later(() => { alive = false; cancelAnimationFrame(raf); endGame('meteor', pool, score); }, 1200); };

    const first = shuffle(pool).slice(0, N); while (first.length < N && fillers.length) { const f = fillers[Math.floor(Math.random() * fillers.length)]!; if (!first.includes(f) && !first.some((x) => x.uz === f.uz)) first.push(f); }
    first.forEach((o) => spawn(o, false));
    newTarget(); last = performance.now(); raf = requestAnimationFrame(loop);

    return () => { alive = false; cancelAnimationFrame(raf); timers.forEach(clearTimeout); rocks.forEach((r) => r.el.remove()); rocks = []; };
  }, []); // eslint-disable-line

  return (
    <ArcadeShell title="Meteor" stat={
      <span className="row g8">
        <span className="pill" style={{ height: 30, background: 'var(--primary-soft)', color: 'var(--primary-ink)', fontSize: 13 }}>{tn('#-daraja', level)}</span>
        <b className="hearts">{'♥'.repeat(Math.max(0, lives)) + '♡'.repeat(3 - Math.max(0, lives))}</b>
      </span>
    }>
      <div className="prompt-card">
        <div className="col g4" style={{ minWidth: 0 }}>
          <span className="cap" style={{ fontSize: 11 }}>{t('Tarjimasini otib tushiring')}</span>
          <b id="pw" className="target-word" ref={pw} />
        </div>
        <span className="row g8">
          <span className="pill" style={{ height: 32, background: 'var(--peach-soft)', color: 'var(--peach-ink)' }}><Ic n="zap" size={16} /><span>{score}</span></span>
          <button className="spk" aria-label={t('Tinglash')} onClick={() => curRef.current && speak(curRef.current.w)}><Ic n="vol" /></button>
        </span>
      </div>
      <div className="tbar"><i ref={tb} /></div>
      <div className="sky tall" ref={sky}>
        <div className="planet" />
        <div className="cannon" ref={cn}><div className="barrel" ref={br}><i /></div><div className="cbase" /></div>
        <div className="lvup" ref={lu} hidden />
      </div>
      <p className="t12" style={{ margin: 0, textAlign: 'center' }}>{t('Toʻgʻri tarjimani bosing — toʻp uni otib tushiradi')}</p>
    </ArcadeShell>
  );
}

/* ---------- Yakun ---------- */
export function ArcadeEnd({ title, score, best, isRec, xp, again }: { title: string; score: number; best: number; isRec: boolean; xp: number; again: () => void }) {
  const streak = useStore((s) => s.streak);
  useEffect(() => { if (isRec) chime(); }, [isRec]);
  return (
    <>
      <div className="content fade" style={{ paddingTop: 40, gap: 18, alignItems: 'center', textAlign: 'center' }}>
        <div className="flamebig"><Ic n={title === 'Meteor' ? 'rocket' : 'timer'} /></div>
        <h1 className="h2">{tn(`${title}: # ball`, score)}</h1>
        {isRec
          ? <span className="soon" style={{ background: 'var(--success-soft)', color: 'var(--success-ink)', fontSize: 15, padding: '6px 14px' }}>{t('Yangi rekord!')}</span>
          : <span className="soon" style={{ fontSize: 15, padding: '6px 14px' }}>{tn('Rekord: #', best)}</span>}
        <div className="stat3 center" style={{ width: '100%' }}>
          <div><b style={{ color: 'var(--primary-ink)' }}>+{xp}</b><span>XP</span></div>
          <div><b>{score}</b><span>{t('ball')}</span></div>
          <div><b style={{ color: 'var(--peach-ink)' }}>{streak}</b><span>{t('kun streak')}</span></div>
        </div>
      </div>
      <div className="sfoot">
        <button className="btn soft" onClick={again}>{t('Yana oʻynash')}</button>
        <button className="btn" onClick={() => nav.home('games')}>{t('Oʻyinlarga')}</button>
      </div>
    </>
  );
}
